import {
    type Placement as FloatingPlacement,
    autoUpdate,
    flip as flipMiddleware,
    limitShift,
    offset as offsetMiddleware,
    shift as shiftMiddleware,
    useFloating,
} from '@floating-ui/react';
import classNames from 'classnames';
import { IconX } from 'featherico';
import type { FC, MouseEventHandler, ReactNode } from 'react';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { pxToNumber } from '../../../common/services/number-formatter';
import Button from '../Button';
import type { ArrowPosition } from './Arrow';
import Arrow from './Arrow';
import styles from './style.m.less';

export type Placement = ArrowPosition;

const DEFAULT_PLACEMENT = 'top';
const CALLOUT_ARROW_SIZE = 5;
const ARROW_POSITIONS: { [key in NonNullable<FloatingPlacement>]?: ArrowPosition } = {
    left: 'right',
    right: 'left',
    'top-start': 'bottom-start',
    top: 'bottom',
    'top-end': 'bottom-end',
    'bottom-start': 'top-start',
    bottom: 'top',
    'bottom-end': 'top-end',
};

const getArrowPosition = (placement: FloatingPlacement): ArrowPosition => {
    const arrowPosition = placement ? ARROW_POSITIONS[placement] : 'top';
    // Our Arrow doesn't support all positions yet, so we default to 'top' for unsupported ones.
    return arrowPosition || 'top';
};

const CloseButton = ({ onClick }: { onClick: MouseEventHandler<HTMLButtonElement> }) => (
    <div className={styles.closeButtonContainer}>
        <Button kind="PLAIN" onClick={onClick} aria-label="Close Popover" className={styles.closeIcon}>
            <IconX />
        </Button>
    </div>
);

export type PopoverProps = {
    title?: ReactNode;
    content: ReactNode;
    children?: ReactNode;
    isShown: boolean;
    /** Adds a close button that triggers the provided callback function. */
    onClose?: MouseEventHandler<HTMLButtonElement>;
    placement?: ArrowPosition;
    offset?: number;
    minWidth?: string | number;
    className?: string;
    showArrow?: boolean;
    /** Stretch the container of the children to the full width. */
    containerFullWidth?: boolean;
    id?: string;
};

const Popover: FC<PopoverProps> = ({
    title,
    content,
    children,
    isShown,
    onClose,
    placement = DEFAULT_PLACEMENT,
    offset = 0,
    minWidth = '150px',
    className = undefined,
    showArrow = true,
    containerFullWidth = false,
    id,
}) => {
    const { ref, topBarHeight } = useTopBarHeight(isShown);
    const detectOverflowOptions = useMemo(() => {
        const basePadding = 5;
        return {
            padding: {
                top: Number.isFinite(topBarHeight) ? topBarHeight + basePadding : basePadding,
                right: basePadding,
                bottom: basePadding,
                left: basePadding,
            },
        };
    }, [topBarHeight]);
    const {
        refs,
        floatingStyles,
        placement: calculatedPlacement,
    } = useFloating({
        placement,
        middleware: [
            offsetMiddleware(CALLOUT_ARROW_SIZE + offset),
            flipMiddleware(detectOverflowOptions),
            shiftMiddleware({ ...detectOverflowOptions, crossAxis: true, limiter: limitShift() }),
        ],
        whileElementsMounted: autoUpdate,
    });

    return (
        <div
            ref={ref}
            className={classNames(styles.container, {
                [styles.containerFullWidth]: containerFullWidth,
            })}
        >
            <div ref={refs.setReference}>{children}</div>
            {isShown && (
                <div
                    // FIXME
                    // eslint-disable-next-line react-hooks/refs
                    ref={refs.setFloating}
                    className={classNames(styles.popoverContainer, className)}
                    style={{ ...floatingStyles, minWidth }}
                    // Isolate click (to prevent bubbling to the outside, at least in the React realm).
                    onClick={event => event.stopPropagation()}
                    role="presentation"
                    id={id}
                >
                    {title && <h4 className={styles.title}>{title}</h4>}
                    {!!onClose && <CloseButton onClick={onClose} />}
                    <div>{content}</div>
                    {showArrow && <Arrow position={getArrowPosition(calculatedPlacement)} />}
                </div>
            )}
        </div>
    );
};

function useTopBarHeight(isShown: boolean) {
    const ref = useRef<HTMLDivElement>(null);
    const [topBarHeight, setTopBarHeight] = useState(0);

    useLayoutEffect(() => {
        if (ref.current && isShown) {
            const value = pxToNumber(getComputedStyle(ref.current).getPropertyValue('--topBarHeight'));
            setTopBarHeight(Number.isFinite(value) ? value : 0);
        }
    }, [isShown]);

    return { ref, topBarHeight };
}

export default Popover;
